import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import useAdminTheme from "../hooks/useAdminTheme";

const invalidResetLinkMessage = "This reset link is invalid or expired.";

function UpdatePasswordPage() {
  const navigate = useNavigate();
  const { isDark } = useAdminTheme();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingSession, setLoadingSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invalidResetLink, setInvalidResetLink] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadRecoverySession = async () => {
      setError("");

      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const searchParams = new URLSearchParams(window.location.search);
      const accessToken = hashParams.get("access_token") || searchParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token") || searchParams.get("refresh_token");
      const code = searchParams.get("code");
      const urlError = hashParams.get("error") || searchParams.get("error");
      const urlErrorCode = hashParams.get("error_code") || searchParams.get("error_code");
      const urlErrorDescription = hashParams.get("error_description") || searchParams.get("error_description");

      if (
        urlError === "access_denied" ||
        urlErrorCode === "otp_expired" ||
        urlErrorDescription?.toLowerCase().includes("invalid or has expired")
      ) {
        setInvalidResetLink(true);
        setError(invalidResetLinkMessage);
        setLoadingSession(false);
        return;
      }

      try {
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            throw sessionError;
          }
        } else if (code) {
          const { error: codeError } = await supabase.auth.exchangeCodeForSession(code);

          if (codeError) {
            throw codeError;
          }
        }

        const { data, error: currentSessionError } = await supabase.auth.getSession();

        if (currentSessionError) {
          throw currentSessionError;
        }

        if (!data.session) {
          setInvalidResetLink(true);
          setError(invalidResetLinkMessage);
        }
      } catch (sessionError) {
        console.error("Supabase password reset session error:", sessionError);
        setInvalidResetLink(true);
        setError(invalidResetLinkMessage);
      } finally {
        if (mounted) {
          setLoadingSession(false);
        }
      }
    };

    loadRecoverySession();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const redirectTimer = setTimeout(() => {
      navigate("/admin-login");
    }, 2500);

    return () => clearTimeout(redirectTimer);
  }, [navigate, success]);

  const validatePassword = () => {
    if (!newPassword) {
      return "Password is required.";
    }

    if (newPassword.length < 8) {
      return "Password must be at least 8 characters.";
    }

    if (newPassword !== confirmPassword) {
      return "Passwords do not match.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    const validationError = validatePassword();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setSubmitting(false);

    if (updateError) {
      console.error("Supabase password update error:", updateError);
      setError("Unable to update password. Please try again.");
      return;
    }

    setSuccess("Password updated successfully.");
    setNewPassword("");
    setConfirmPassword("");
  };

  const ui = {
    page: isDark ? "bg-black text-white" : "bg-slate-50 text-slate-950",
    card: isDark
      ? "border-white/10 bg-white/[0.04] shadow-black/40"
      : "border-slate-200 bg-white shadow-slate-200/70",
    title: isDark ? "text-yellow-400" : "text-slate-950",
    muted: isDark ? "text-neutral-400" : "text-slate-500",
    notice: isDark
      ? "bg-white/[0.04] text-neutral-300"
      : "bg-slate-50 text-slate-600",
    input: isDark
      ? "border-white/10 bg-neutral-900 text-white placeholder:text-neutral-500"
      : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white",
    link: isDark
      ? "border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]"
      : "border-slate-200 bg-white text-slate-800 hover:border-yellow-300 hover:bg-yellow-50",
  };

  return (
    <main className={`flex min-h-screen items-center justify-center px-6 py-10 ${ui.page}`}>
      <Helmet>
        <title>Update Password | Limassol Airport Taxi</title>
        <meta
          name="description"
          content="Set a new admin password for Limassol Airport Taxi."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className={`w-full max-w-md rounded-3xl border p-6 shadow-2xl sm:p-8 ${ui.card}`}>
        <h1 className={`text-3xl font-bold ${ui.title}`}>
          Update Password
        </h1>

        <p className={`mt-3 ${ui.muted}`}>
          Set a new admin password to continue.
        </p>

        {loadingSession ? (
          <p className={`mt-8 rounded-xl px-4 py-3 text-sm font-semibold ${ui.notice}`}>
            Checking password reset link...
          </p>
        ) : invalidResetLink ? (
          <div className="mt-8 space-y-4">
            <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              {error}
            </p>

            <Link
              to="/admin-login"
              className={`block w-full rounded-2xl border px-6 py-4 text-center font-bold transition ${ui.link}`}
            >
              Back to Admin Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`w-full rounded-2xl border px-4 py-4 outline-none transition focus:border-yellow-400 ${ui.input}`}
              placeholder="New password"
              autoComplete="new-password"
            />

            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full rounded-2xl border px-4 py-4 outline-none transition focus:border-yellow-400 ${ui.input}`}
              placeholder="Confirm password"
              autoComplete="new-password"
            />

            {error && (
              <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
                {error}
              </p>
            )}

            {success && (
              <p className="rounded-xl bg-green-500/10 px-4 py-3 text-sm font-semibold text-green-400">
                {success}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || Boolean(success)}
              className="w-full rounded-2xl bg-yellow-400 px-6 py-4 font-bold text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? "Updating..." : "Update Password"}
            </button>

            {success && (
              <Link
                to="/admin-login"
                className={`block w-full rounded-2xl border px-6 py-4 text-center font-bold transition ${ui.link}`}
              >
                Go to Admin Login
              </Link>
            )}

            {!success && (
              <Link
                to="/admin-login"
                className={`block w-full rounded-2xl border px-6 py-4 text-center font-bold transition ${ui.link}`}
              >
                Back to Admin Login
              </Link>
            )}
          </form>
        )}
      </div>
    </main>
  );
}

export default UpdatePasswordPage;
